import { useState, type FC } from 'react';
import { LifecycleStatusTag } from 'components/lifecycle-status-tag';
import { Checkbox, Button, InlineNotification } from '@carbon/react';
import { Add, Close, Copy, Edit } from '@carbon/react/icons';
import classNames from 'classnames';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from 'components/accordion/accordion';
import {
  ColorSwatch,
  DEFAULT_COLORS,
  hexToRgba,
} from 'components/color-picker';
import {
  ColorByAttributeModal,
  type ColorByRule,
  type ColorByOption,
} from 'components/color-by-attribute-modal';
import {
  PRE_DRILL_SECTIONS,
  PRE_DRILL_COLOR_CATEGORIES,
  PRE_DRILL_COLOR_TARGETS,
  PRE_DRILL_COLOR_NEW_RULE_APPLY_TO,
  PRE_DRILL_COLOR_RESET_APPLY_TO,
  type PreDrillSectionId,
} from './utils';
import styles from './settings-panel.module.scss';

interface SettingsPanelProps {
  structureGridCollectionName: string | null;
  /** True when the selected SGC is the wellbore default (shows a "Default" tag). */
  structureGridCollectionIsDefault: boolean;
  savingStructureGridCollection: boolean;
  /** Hides the Wellbore Points toggle on a backend that serves no points. */
  hasWellborePoints: boolean;
  /** Hides the Related Items toggle when no Well is mapped (section absent). */
  hasRelatedItems: boolean;
  visibility: Record<PreDrillSectionId, boolean>;
  colorRules: ColorByRule[];
  disabled: boolean;
  onVisibilityChange: (next: Record<PreDrillSectionId, boolean>) => void;
  onColorRulesChange: (next: ColorByRule[]) => void;
  /** Resolves matching wells for the attribute's selected values (map color). */
  onColorSelectionChange: (categoryId: string, values: string[]) => void;
  /** Per-attribute value list from the wells facets call (Well Qualifier static). */
  getColorOptions: (categoryId: string) => ColorByOption[];
  /** Whether a facets fetch is in flight (drives the modal's loading state). */
  colorOptionsLoading: boolean;
  /** Color modal open: triggers the bbox-scoped facets fetch for value lists. */
  onOpenColorModal: () => void;
  /** Opens the Structure Grid Collection picker (workspace right drawer). */
  onOpenGridCollection: () => void;
}

// Map a color-by category / apply-target id to its display label.
function categoryLabel(attribute: string): string {
  return (
    PRE_DRILL_COLOR_CATEGORIES.find((c) => c.id === attribute)?.label ??
    attribute
  );
}

function targetLabel(target: string): string {
  return PRE_DRILL_COLOR_TARGETS.find((t) => t.id === target)?.label ?? target;
}

// Settings sidebar (left of DualSidebarLayout): accordion sections with
// color-rule rows. View settings + color rules are parent local state.
export const SettingsPanel: FC<SettingsPanelProps> = ({
  structureGridCollectionName,
  structureGridCollectionIsDefault,
  savingStructureGridCollection,
  hasRelatedItems,
  hasWellborePoints,
  visibility,
  colorRules,
  disabled,
  onVisibilityChange,
  onColorRulesChange,
  onColorSelectionChange,
  getColorOptions,
  colorOptionsLoading,
  onOpenColorModal,
  onOpenGridCollection,
}) => {
  const [colorModalOpen, setColorModalOpen] = useState(false);
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null);

  const toggleSection = (id: PreDrillSectionId, checked: boolean) => {
    onVisibilityChange({ ...visibility, [id]: checked });
  };

  const openColorModal = (ruleId: string | null) => {
    setEditingRuleId(ruleId);
    setColorModalOpen(true);
    // Pull bbox-scoped facet value lists (read via getColorOptions); refreshes
    // to the current viewport on each open.
    onOpenColorModal();
  };

  const removeRule = (ruleId: string) => {
    onColorRulesChange(colorRules.filter((rule) => rule.ruleId !== ruleId));
  };

  const duplicateRule = (rule: ColorByRule, index: number) => {
    const copy: ColorByRule = {
      ...rule,
      ruleId: `${rule.attribute}-copy-${index}-${colorRules.length}`,
      valueColors: { ...rule.valueColors },
      applyTo: { ...rule.applyTo },
      enabled: true,
    };
    onColorRulesChange([...colorRules, copy]);
  };

  const toggleEnabled = (ruleId: string) => {
    onColorRulesChange(
      colorRules.map((rule) => {
        if (rule.ruleId !== ruleId) return rule;
        return { ...rule, enabled: !rule.enabled };
      })
    );
  };

  const changeValueColor = (
    ruleId: string,
    value: string,
    color: string,
    opacity: number
  ) => {
    onColorRulesChange(
      colorRules.map((rule) => {
        if (rule.ruleId !== ruleId) return rule;
        return {
          ...rule,
          valueColors: {
            ...rule.valueColors,
            [value]: hexToRgba(color, opacity),
          },
        };
      })
    );
  };

  return (
    <div className={styles.panel}>
      <Accordion defaultExpanded>
        <AccordionSummary>
          <div className={styles.filterSectionTitle}>Workspace Settings</div>
        </AccordionSummary>
        <AccordionDetails>
          <div className={styles.filterSection}>
            <div className={styles.sgcRow}>
              <div className={styles.settingRow}>
                <span className={styles.settingLabel}>
                  Structure Grid Collection
                </span>
                <span className={styles.settingValue}>
                  {savingStructureGridCollection
                    ? 'Updating…'
                    : (structureGridCollectionName ?? 'Not selected')}
                  {!savingStructureGridCollection &&
                    structureGridCollectionIsDefault && (
                      <LifecycleStatusTag
                        status="default"
                        className={styles.sgcDefaultTag}
                      />
                    )}
                </span>
              </div>
              <Button
                kind="ghost"
                size="sm"
                hasIconOnly
                renderIcon={Edit}
                iconDescription="Change Structure Grid Collection"
                tooltipPosition="bottom"
                disabled={disabled || savingStructureGridCollection}
                onClick={onOpenGridCollection}
              />
            </div>
            {!savingStructureGridCollection &&
              structureGridCollectionName === null && (
                <InlineNotification
                  kind="warning"
                  lowContrast
                  hideCloseButton
                  className={styles.sgcWarningNotification}
                  title="Not selected"
                  subtitle="Needed for AI suggestions."
                />
              )}
          </div>
        </AccordionDetails>
      </Accordion>

      <Accordion defaultExpanded>
        <AccordionSummary>
          <div className={styles.filterSectionTitle}>View Settings</div>
        </AccordionSummary>
        <AccordionDetails>
          <div className={styles.filterSection}>
            <div className={styles.checkboxGroup}>
              {PRE_DRILL_SECTIONS.filter(
                (section) =>
                  (section.id !== 'relatedFiles' || hasRelatedItems) &&
                  (section.id !== 'wellborePoints' || hasWellborePoints)
              ).map((section) => (
                <Checkbox
                  key={section.id}
                  id={`pre-drill-visibility-${section.id}`}
                  labelText={section.label}
                  checked={visibility[section.id]}
                  disabled={disabled}
                  onChange={(_evt, { checked }) =>
                    toggleSection(section.id, checked)
                  }
                />
              ))}
            </div>
          </div>
        </AccordionDetails>
      </Accordion>

      <Accordion defaultExpanded>
        <AccordionSummary>
          <div className={styles.filterSectionTitle}>Color by Attribute</div>
        </AccordionSummary>
        <AccordionDetails>
          <div className={styles.filterSection}>
            <div className={styles.wellsFilterActions}>
              <Button
                kind="tertiary"
                size="sm"
                renderIcon={Add}
                disabled={disabled}
                onClick={() => openColorModal(null)}
              >
                Color by Attribute
              </Button>
            </div>

            {colorRules.length === 0 ? (
              <p className={styles.emptyFiltersText}>
                No color rules have been added yet
              </p>
            ) : (
              <div className={styles.appliedColorByList}>
                {colorRules.map((rule, index) => {
                  const activeTargets = Object.entries(rule.applyTo)
                    .filter(([, active]) => active)
                    .map(([key]) => targetLabel(key));

                  return (
                    <div key={rule.ruleId} className={styles.appliedColorBy}>
                      <div className={styles.appliedFilterHeader}>
                        <span className={styles.appliedFilterName}>
                          {categoryLabel(rule.attribute)}
                        </span>
                        <div className={styles.appliedFilterActions}>
                          <button
                            type="button"
                            className={classNames(
                              styles.polygonAction,
                              styles.polygonActionEdit
                            )}
                            onClick={() => openColorModal(rule.ruleId)}
                            aria-label="Edit rule"
                            disabled={disabled}
                          >
                            <Edit />
                          </button>
                          <button
                            type="button"
                            className={classNames(
                              styles.polygonAction,
                              styles.polygonActionEdit
                            )}
                            onClick={() => duplicateRule(rule, index)}
                            aria-label="Duplicate rule"
                            disabled={disabled}
                          >
                            <Copy />
                          </button>
                          <button
                            type="button"
                            className={styles.polygonAction}
                            onClick={() => removeRule(rule.ruleId)}
                            aria-label="Remove rule"
                            disabled={disabled}
                          >
                            <Close />
                          </button>
                        </div>
                      </div>

                      <div className={styles.ruleControlsRow}>
                        {activeTargets.length > 0 && (
                          <div className={styles.colorByActiveOn}>
                            <span className={styles.colorByActiveLabel}>
                              Active on:
                            </span>
                            <div className={styles.appliedFilterChips}>
                              {activeTargets.map((target) => (
                                <span
                                  key={target}
                                  className={styles.appliedFilterChip}
                                >
                                  {target}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        <Button
                          kind="ghost"
                          size="sm"
                          disabled={disabled}
                          onClick={() => toggleEnabled(rule.ruleId)}
                        >
                          {rule.enabled ? 'Disable' : 'Enable'}
                        </Button>
                      </div>

                      {rule.selectedValues.length > 0 && (
                        <div className={styles.colorByValues}>
                          {rule.selectedValues.map((value, valueIndex) => (
                            <div key={value} className={styles.colorByValueRow}>
                              <ColorSwatch
                                color={
                                  rule.valueColors[value] ??
                                  DEFAULT_COLORS[
                                    valueIndex % DEFAULT_COLORS.length
                                  ]
                                }
                                onChange={(
                                  newColor: string,
                                  newOpacity: number
                                ) =>
                                  changeValueColor(
                                    rule.ruleId,
                                    value,
                                    newColor,
                                    newOpacity
                                  )
                                }
                              />
                              <span>
                                {rule.selectedValueLabels?.[valueIndex] ??
                                  value}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </AccordionDetails>
      </Accordion>

      <ColorByAttributeModal
        open={colorModalOpen}
        onClose={() => setColorModalOpen(false)}
        editingRuleId={editingRuleId}
        rules={colorRules}
        onApply={onColorRulesChange}
        categories={PRE_DRILL_COLOR_CATEGORIES}
        getOptions={getColorOptions}
        applyToTargets={PRE_DRILL_COLOR_TARGETS}
        newRuleApplyTo={PRE_DRILL_COLOR_NEW_RULE_APPLY_TO}
        resetApplyTo={PRE_DRILL_COLOR_RESET_APPLY_TO}
        onSelectionChange={onColorSelectionChange}
        isFetching={colorOptionsLoading}
        loadingDescription="Loading well data..."
      />
    </div>
  );
};
